import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Box } from "@mui/material";
import { useMassUploadsList } from "my-documents/hooks";
import { DELETE_MASS_UPLOAD } from "my-documents/graphql";
import { formatDateTime } from "my-documents/utils";
import {
  massUploadHistoryTableDef,
  massUploadHistoryMobileTableDef,
} from "my-documents/configs/table-defs";
import { OcCard, OcDataTable } from "oc/components";
import { useOcIsMobile, useOcTableState, useOcSnackbar } from "oc/hooks";
import { initialTableData } from "oc/configs/initial-table-data";

export const MyDocumentsMassUploadHistory = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();
  const { setOpenMessage } = useOcSnackbar();
  const { data, loading, error, loadQuery } = useMassUploadsList();
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );

  const [deleteMassUpload] = useMutation(DELETE_MASS_UPLOAD, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_DOCUMENTS_DOCUMENT_SUCCESSFUL_DELETION"),
      });
      loadQuery();
    },
    onError: () => {
      setOpenMessage({
        type: "error",
        message: t("MY_DOCUMENTS_DOCUMENT_ERROR_DELETION"),
      });
    },
  });

  const tableId = "massUploadHistoryTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const handleRowSelection = (row: { massUploadId: string }) => {
    const selectedId =
      selectedRowId === row?.massUploadId ? undefined : row?.massUploadId;
    setSelectedRowId(selectedId);
  };

  const handleTableDataChange = (state: typeof tableData) => {
    setTableData(state);
  };

  const handleRemoveMassUpload = (row: { massUploadId: string }) => {
    deleteMassUpload({
      variables: {
        massUploadId: row.massUploadId,
      },
    });
  };

  // Transform data for table
  const tableRows = data.map((item: any, index: number) => ({
    _id: item.massUploadId,
    massUploadId: item.massUploadId,
    employee: item.uploader.employee,
    name: item.uploader.name,
    rawName: item.uploader.rawName,
    contract: item.uploader.contract,
    orgUnit: item.uploader.orgUnit || "-",
    uploadedAt: formatDateTime(item.uploadedAt, i18n.language),
    documentCount: item.documentCount || 0,
    documents: item.documents || [],
    rowIndex: index,
  }));

  const tableDef = isMobile
    ? massUploadHistoryMobileTableDef
    : massUploadHistoryTableDef;

  const filteredTableDef = {
    id: massUploadHistoryTableDef?.id,
    columns: massUploadHistoryTableDef?.columns?.filter(
      (column) =>
        !massUploadHistoryMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  if (!tableRows || tableRows.length === 0) {
    return null;
  }

  return (
    <OcCard
      label={t("MY_DOCUMENTS_MASS_UPLOAD_HISTORY")}
      loading={loading}
      error={!!error}
    >
      <Box height="16px" />
      <OcDataTable
        tableDef={tableDef}
        tableId={tableId}
        tableData={tableData}
        setTableData={handleTableDataChange}
        data={{ rows: tableRows, total: tableRows.length }}
        loading={loading}
        selectedRowId={selectedRowId}
        onSelectRow={handleRowSelection}
        handleMoreButton={handleRowSelection}
        pagination={true}
        localPaging={true}
        filteredTableDef={filteredTableDef}
        myDocuments={isMobile ? false : true}
        isNotExaminer={false}
        removeMassUpload={handleRemoveMassUpload}
      />
      <Box height="16px" />
    </OcCard>
  );
};

export default MyDocumentsMassUploadHistory;
